/** ログインの結果。拒否（401・403 など）は例外で返す。 */
export type LoginOutcome = 'ok' | 'not_manager' | 'cookie_unavailable' | 'network';

/**
 * YAC-2026-009 PR5a。運営の2段階認証の段（POST /v1/web/token などの 202 の mfa）。
 *  - password_change_required  初めてのログイン。初期パスワードを変える
 *  - email_code_required       メールで届いた6桁のコード
 *  - setup_required            認証アプリの登録（QR と最初のコード）
 *  - totp_required             認証アプリの6桁のコード
 */
export type MfaStage =
  | 'password_change_required'
  | 'email_code_required'
  | 'setup_required'
  | 'totp_required';
const MFA_STAGES: readonly MfaStage[] = [
  'password_change_required',
  'email_code_required',
  'setup_required',
  'totp_required',
];

/**
 * 2段階認証のチャレンジ。token（mfa_token）は段ごとに替わる（送り直しでは替わらない）。
 * 画面のメモリ（React の state）だけに持ち、localStorage・sessionStorage・URL・履歴には置かない。
 */
export type MfaChallenge = { stage: MfaStage; token: string; expiresIn: number };

/** パスワードは正しく、運営の2段階認証へ進む（トークンはまだ無い）。 */
export type MfaPending = { kind: 'mfa'; challenge: MfaChallenge };
export type LoginResult = LoginOutcome | MfaPending;

/** 認証アプリの登録に使う値（POST /v1/web/token/mfa/setup の 200）。 */
export type MfaSetupDetails = {
  secret: string;
  otpauthUri: string;
  /** QR の画像（data:image/svg+xml の data URI）。img の src にだけ使う（HTML として埋め込まない）。 */
  qrSvg: string;
  expiresIn: number;
};

/** YAC-2026-009 PR3 の経路（サーバー: api/mfa_views.py）。 */
export const MFA_PATHS = {
  initialPassword: '/v1/web/token/password',
  emailCode: '/v1/web/token/mfa/email',
  emailResend: '/v1/web/token/mfa/email/resend',
  setup: '/v1/web/token/mfa/setup',
  setupConfirm: '/v1/web/token/mfa/setup/confirm',
  totp: '/v1/web/token/mfa',
} as const;

/** サーバーの応答が、2段階認証の決まった形でない（状態・本文）。 */
export class UnexpectedMfaResponseError extends Error {
  constructor() {
    super('The sign-in response was not in the expected form');
    this.name = 'UnexpectedMfaResponseError';
  }
}

/**
 * #385 の Codex の指摘 4368093977。画面がこの試行をやめた（「ログインへ戻る」・段の作り直し・別のログイン）。
 * 結果は使わない。最後の段では、受け取ったトークンを保存せず、Cookie も sid を添えて失効させる。
 */
export class MfaAbortedError extends Error {
  constructor() {
    super('The sign-in attempt was abandoned');
    this.name = 'MfaAbortedError';
  }
}

/** 2段階認証の呼び出しの任意の指定。signal が abort されたら、結果を捨てて MfaAbortedError にする。 */
export type MfaCallOptions = { signal?: AbortSignal };

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new MfaAbortedError();
}

/** 202 の本文（{mfa, mfa_token, expires_in}）を読む。形が違えば UnexpectedMfaResponseError。 */
export function readChallenge(response: { status?: number; data?: any } | undefined): MfaChallenge {
  const data = response?.data;
  const stage = data?.mfa;
  const token = data?.mfa_token;
  if (
    response?.status !== 202 ||
    !(MFA_STAGES as readonly string[]).includes(stage) ||
    typeof token !== 'string' ||
    token === ''
  ) {
    throw new UnexpectedMfaResponseError();
  }
  const expiresIn = typeof data.expires_in === 'number' ? data.expires_in : 0;
  return { stage: stage as MfaStage, token, expiresIn };
}
